import React, { useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Divider from 'components/divider';
import { isLightTheme } from 'lib/themes/theme';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PatientDischarge from './patient-discharge';
import PatientReenroll from './patient-reenroll';
import { TaskTypesEnum, CounselingTaskType } from '../../../constants/enums';

const useStyles = makeStyles(theme => ({
  patientInfoContainer: {
    margin: '15px 30px 30px 30px',
    borderRadius: 5,
    paddingBottom: theme.spacing(2),
    boxShadow: `1px 1px 3px 0px rgba(0, 0, 0, 0.2),
      -1px 1px 1px 0px rgba(0, 0, 0, 0.14),
      0px 2px 1px -1px rgba(0, 0, 0, 0.12)`,
    backgroundColor: isLightTheme() ? 'transparent' : theme.palette.primary.grey14,
  },
  patientStatusTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  activeClinicRow: {
    backgroundColor: theme.palette.primary.grey14,
    marginBottom: theme.spacing(-2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  dischargedClinicRow: {
    backgroundColor: theme.palette.primary.grey14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

export const PatientStatus = ({
  patient,
  patientClinicsDischarged,
  patientClinicsNotDischarged,
}) => {
  const classes = useStyles();
  const tasks = useSelector(state => state.tasks.data);
  const therapies = useSelector(state => state.therapies.data);
  const dischargeCslList = (Object.values(tasks) || []).filter(
    t =>
      t.taskType === TaskTypesEnum.CSL &&
      t.counseling_type === CounselingTaskType.Discharge &&
      t.canceled_dt == null &&
      t.completed_dt == null,
  );
  const dischargeCslClinicAndDate = dischargeCslList
    .map(disCsl => {
      return {
        therapy: therapies[disCsl?.therapy_id],
        cslCreatedDt: disCsl.created,
      };
    })
    .map(tt => {
      return {
        clinic_id: tt.therapy?.managing_clinic_id,
        discharge_dt: moment(tt.cslCreatedDt).format('MM/DD/YYYY') ?? '',
      };
    });
  const dischargeCslManagingClinicsId = dischargeCslClinicAndDate.map(t => t.clinic_id);

  return (
    <Grid item xs={12} className={classes.patientInfoContainer}>
      <Grid container alignItems="center">
        <Grid item xs={12}>
          <Grid container justifyContent="flex-start" alignItems="center">
            <Typography className={classes.patientStatusTitle}>Patient Status:</Typography>
          </Grid>
          <Divider />
        </Grid>

        <Grid item xs={12} className={classes.activeClinicRow}>
          {patientClinicsNotDischarged?.length
            ? patientClinicsNotDischarged.map(clinic => (
                <PatientDischarge
                  key={`${patient.id}-clinic-${clinic.id}`}
                  clinicId={clinic.id}
                  verifyValue={clinic}
                />
              ))
            : null}
        </Grid>

        <Grid item xs={12} className={classes.dischargedClinicRow}>
          {patientClinicsDischarged?.length &&
          patientClinicsDischarged.filter(pcd => dischargeCslManagingClinicsId.includes(pcd.id))
            ? patientClinicsDischarged
                .filter(pcd => dischargeCslManagingClinicsId.includes(pcd.id))
                .map(clinic => (
                  <PatientDischarge
                    key={`${patient.id}-clinic-${clinic.id}`}
                    clinic={clinic}
                    verifyValue={clinic}
                    disabled
                    dischargeDate={
                      dischargeCslClinicAndDate.find(t => t.clinic_id === clinic.id)
                        ?.discharge_dt ?? ''
                    }
                  />
                ))
            : null}
        </Grid>

        <Grid item xs={12} className={classes.dischargedClinicRow}>
          {patientClinicsDischarged?.length &&
          patientClinicsDischarged.filter(pcd => !dischargeCslManagingClinicsId.includes(pcd.id))
            ? patientClinicsDischarged
                .filter(pcd => !dischargeCslManagingClinicsId.includes(pcd.id))
                .map(clinic => (
                  <PatientReenroll
                    key={`${patient.id}-clinic-${clinic.id}`}
                    clinic={clinic}
                    patient={patient}
                  />
                ))
            : null}
        </Grid>

        {!patient.deceased && (
          <Grid item xs={12} className={classes.activeClinicRow}>
            <PatientDischarge
              verifyValue={{ name: `${patient.first_name} ${patient.last_name}`, id: patient.id }}
              isDeceasedType
            />
          </Grid>
        )}

        {!!patient.deceased && (
          <Grid item xs={12} className={classes.deceasedPatient}>
            <Typography variant="body2">Patient is deceased</Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default PatientStatus;
